import React, { useState, useRef, useLayoutEffect, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import styles from "./SideNavBar.module.css";

const GAP = 14;
const MARGIN = 14;

const NavInfoButton = ({ label, render }) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const cardRef = useRef(null);
  const location = useLocation();

  const close = useCallback(() => setOpen(false), []);

  // position: below the icon, flip above if no room, clamp to viewport
  useLayoutEffect(() => {
    if (!open) return;

    const place = () => {
      const btn = btnRef.current;
      const card = cardRef.current;
      if (!btn) return;

      const b = btn.getBoundingClientRect();
      const cw = card?.offsetWidth ?? 0;
      const ch = card?.offsetHeight ?? 0;

      let left = b.right + GAP;
      let top = b.bottom + GAP;

      if (left + cw > window.innerWidth - MARGIN) {
        left = window.innerWidth - cw - MARGIN;
      }
      if (top + ch > window.innerHeight - MARGIN) {
        top = Math.max(MARGIN, b.top - ch - GAP);
      }
      setPos({ left, top });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  // outside click + Escape
  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (cardRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => e.key === "Escape" && close();

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  // close on navigation
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`${styles.infoBtn} ${open ? styles.infoBtnActive : ""}`}
        aria-label={`${label} info`}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.stopPropagation();
          setPos(null);
          setOpen((v) => !v);
        }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={cardRef}
            role="dialog"
            aria-label={`${label} info`}
            className={styles.infoPortal}
            style={{
              left: pos?.left ?? 0,
              top: pos?.top ?? 0,
              visibility: pos ? "visible" : "hidden", // avoid flash before measuring
            }}
          >
            {render({ close })}
          </div>,
          document.body,
        )}
    </>
  );
};

export default NavInfoButton;